import onwardColors from 'lib/onwardColors';
import CloseIcon from '@material-ui/icons/Close';
import { Box, IconButton, Typography, makeStyles } from '@material-ui/core';
import { useEffect, useState } from 'react';
import ChatIcon from '../images/ChatIcon';
import ENV from '../constants/Env';
import history from '../navigation/history';

const useStyles = makeStyles(theme => ({
  iframe: {
    minHeight: '100%',
    width: '100%',
    border: 'none',
  },
  container: {
    position: 'fixed',
    bottom: 0,
    right: 0,
    left: 0,
    top: 32,
    backgroundColor: onwardColors.onwardWhite,
    borderRadius: '24px 24px 0 0',
    boxShadow: '0 -1px 16px rgba(0,0,0,0.3)',
    display: 'flex',
    flexDirection: 'column',
    transition: 'transform 0.5s ease-in-out',
    width: '100%',
    zIndex: 99999,
    [theme.breakpoints.up('md')]: {
      width: 500,
      height: '100%',
      left: 'auto',
      top: 'auto',
      borderRadius: 16,
      maxHeight: 840,
    },
    // Landscape mobile: expand full width and height
    '@media (orientation: landscape) and (max-height: 500px)': {
      top: 0,
      maxWidth: 'none',
      width: '100%',
      maxHeight: 'none',
      borderRadius: '0',
    },
  },
  hidden: {
    display: 'none',
  },
  open: {
    transform: 'translateY(0)',
  },
  closed: {
    transform: 'translateY(100%)',
  },
  header: {
    display: 'flex',
    padding: '1rem',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: onwardColors.onwardMedPurple,
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    [theme.breakpoints.up('sm')]: {
      borderTopLeftRadius: 16,
      borderTopRightRadius: 16,
    },
    // No border radius in landscape mobile (full screen)
    '@media (orientation: landscape) and (max-height: 500px)': {
      borderTopLeftRadius: 0,
      borderTopRightRadius: 0,
    },
  },
  left: { display: 'flex', alignItems: 'center' },
  iconWrap: {
    width: 42,
    height: 42,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    backgroundColor: onwardColors.onwardMedPurple,
    marginRight: theme.spacing(1.5),
  },
  title: { color: onwardColors.onwardWhite, fontWeight: 700 },
  subtitle: { color: '#D6BCFA', fontSize: 12 },
  closeBtn: {
    color: onwardColors.onwardWhite,
    backgroundColor: '#4b2862',
    borderRadius: '6px',
    padding: '0.25rem',
    fontSize: '24px',
    '&:hover': { backgroundColor: onwardColors.onwardDarkPurple },
    lineHeight: 1,
  },
  body: { flex: 1 },
}));

export default function ChatPanel({ isOpen, onClose }) {
  const classes = useStyles();
  const [iframeModalOpen, setIframeModalOpen] = useState(false);
  const chatBaseUrl = `${ENV.portalUrl}/chat-support?embeddedInDashboard=true`;

  // Listen for postMessage events from the iframe
  // The iframe notifies us when the MNCS modal opens or closes
  // When the modal is open, we hide the chat panel to avoid
  // overlapping the modal's close / cancel button
  useEffect(() => {
    const handleMessage = event => {
      if (event.origin !== ENV.portalUrl) {
        return;
      }

      const { data } = event;

      if (data.action === 'MNCS_MODAL_OPEN') {
        setIframeModalOpen(true);
      }

      if (data.action === 'MNCS_MODAL_CLOSED') {
        setIframeModalOpen(false);
      }
    };

    window.addEventListener('message', handleMessage, false);

    return () => {
      window.removeEventListener('message', handleMessage, false);
    };
  }, []);

  // Reset iframe modal state on route change
  // The iframe does not always emit a "closed" event when the user
  // navigates away, which can leave the chat panel hidden
  // Listening to navigation ensures the panel becomes visible again
  useEffect(() => {
    const unlisten = history.listen(() => {
      setIframeModalOpen(false);
    });

    return () => {
      unlisten();
    };
  }, []);

  return (
    <Box
      className={`${classes.container} ${
        isOpen ? classes.open : classes.closed
      } ${iframeModalOpen ? classes.hidden : ''}`}
    >
      {/* Header */}
      <Box className={classes.header}>
        <Box className={classes.left}>
          <Box className={classes.iconWrap}>
            <ChatIcon />
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography className={classes.title}>Ask Onward</Typography>
            <Typography className={classes.subtitle}>
              Chat with our AI agent about your rides
            </Typography>
          </Box>
        </Box>

        <IconButton onClick={onClose} className={classes.closeBtn}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Body */}
      <Box className={classes.body}>
        <iframe
          data-test-id="portal-iframe"
          title="Portal Ride Booking"
          src={chatBaseUrl}
          className={classes.iframe}
        />
      </Box>
    </Box>
  );
}
